import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { adminGetPost, adminSavePost } from "@/lib/blogAdmin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NumberInput } from "@/components/ui/number-input";

type Mode = { kind: "new" } | { kind: "edit"; id: string };

export const Route = createFileRoute("/tools/blog/$id")({
  head: () => ({
    meta: [{ title: "Edit blog post" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: () => <Editor mode={{ kind: "edit", id: Route.useParams().id }} />,
});

export type FormState = {
  id?: string;
  slug: string;
  title: string;
  description: string;
  keywords: string;
  category: string;
  body: string;
  read_minutes: number;
  published: boolean;
  published_at: string | null;
};

const empty: FormState = {
  slug: "",
  title: "",
  description: "",
  keywords: "",
  category: "",
  body: "",
  read_minutes: 5,
  published: false,
  published_at: null,
};

export function Editor({ mode }: { mode: Mode }) {
  const navigate = useNavigate();
  const [form, setForm] = useState<FormState>(empty);
  const [loading, setLoading] = useState(mode.kind === "edit");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"write" | "preview">("write");

  useEffect(() => {
    if (mode.kind !== "edit") return;
    (async () => {
      try {
        const row = await adminGetPost({ data: { id: mode.id } });
        if (row) {
          setForm({
            id: row.id,
            slug: row.slug,
            title: row.title,
            description: row.description,
            keywords: row.keywords,
            category: row.category,
            body: row.body,
            read_minutes: row.read_minutes,
            published: row.published,
            published_at: row.published_at,
          });
        }
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [mode]);

  function update<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSave(publishNow?: boolean) {
    setError(null);
    setSaving(true);
    try {
      const payload = { ...form };
      if (publishNow !== undefined) payload.published = publishNow;
      if (payload.published && !payload.published_at) {
        payload.published_at = new Date().toISOString();
      }
      const saved = await adminSavePost({ data: payload });
      if (mode.kind === "new" && saved?.id) {
        navigate({ to: "/tools/blog/$id", params: { id: saved.id } });
      } else {
        setForm((f) => ({ ...f, ...payload }));
      }
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr,320px]">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-bold">
            {mode.kind === "new" ? "New post" : "Edit post"}
          </h1>
          <button
            onClick={() => navigate({ to: "/tools/blog" })}
            className="text-sm text-muted-foreground hover:text-foreground"
          >
            ← Back
          </button>
        </div>

        <div>
          <Label>Title</Label>
          <Input value={form.title} onChange={(e) => update("title", e.target.value)} />
        </div>

        <div>
          <Label>Slug</Label>
          <Input
            value={form.slug}
            onChange={(e) => update("slug", e.target.value)}
            placeholder="my-post-url"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Lowercase letters, numbers, and dashes only. Lives at /blog/{form.slug || "your-slug"}
          </p>
        </div>

        <div>
          <Label>Description (used for SEO + preview)</Label>
          <Textarea
            rows={2}
            value={form.description}
            onChange={(e) => update("description", e.target.value)}
          />
        </div>

        <div className="rounded-lg border border-border">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <div className="flex gap-1">
              <button
                onClick={() => setTab("write")}
                className={`rounded px-3 py-1 text-sm ${tab === "write" ? "bg-muted" : "text-muted-foreground"}`}
              >
                Write
              </button>
              <button
                onClick={() => setTab("preview")}
                className={`rounded px-3 py-1 text-sm ${tab === "preview" ? "bg-muted" : "text-muted-foreground"}`}
              >
                Preview
              </button>
            </div>
            <span className="text-xs text-muted-foreground">Markdown supported</span>
          </div>
          {tab === "write" ? (
            <Textarea
              value={form.body}
              onChange={(e) => update("body", e.target.value)}
              rows={24}
              className="rounded-t-none border-0 font-mono text-sm focus-visible:ring-0"
              placeholder={`## Section heading\n\nWrite your post in **markdown**.\n\n- Bullet one\n- Bullet two`}
            />
          ) : (
            <div className="prose prose-invert min-h-[400px] max-w-none p-4">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {form.body || "*Nothing to preview yet.*"}
              </ReactMarkdown>
            </div>
          )}
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <aside className="space-y-4 rounded-lg border border-border p-4 h-fit">
        <h2 className="font-semibold">Settings</h2>

        <div>
          <Label>Status</Label>
          <p className="mt-1 text-sm">
            {form.published ? (
              <span className="text-primary">Published</span>
            ) : (
              <span className="text-muted-foreground">Draft</span>
            )}
          </p>
        </div>

        <div>
          <Label>Category</Label>
          <Input value={form.category} onChange={(e) => update("category", e.target.value)} />
        </div>

        <div>
          <Label>Read time (minutes)</Label>
          <NumberInput
            min={1}
            max={120}
            value={form.read_minutes}
            onValueChange={(n) => update("read_minutes", n || 5)}
          />
        </div>

        <div>
          <Label>Keywords (SEO)</Label>
          <Textarea
            rows={3}
            value={form.keywords}
            onChange={(e) => update("keywords", e.target.value)}
            placeholder="comma, separated, keywords"
          />
        </div>

        <div className="space-y-2 pt-2">
          <Button onClick={() => onSave()} disabled={saving} className="w-full">
            {saving ? "Saving…" : "Save"}
          </Button>
          {form.published ? (
            <Button
              variant="outline"
              onClick={() => onSave(false)}
              disabled={saving}
              className="w-full"
            >
              Unpublish
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => onSave(true)}
              disabled={saving}
              className="w-full"
            >
              Save & publish
            </Button>
          )}
        </div>
      </aside>
    </div>
  );
}
